import React, { useEffect, useState } from "react";
import PortalLayout, { usePortal } from "../components/PortalLayout";
import { Navigate } from "react-router-dom";
import {
  Users2, FileEdit, Image as ImageIcon, GraduationCap,
  Globe, Mail, Settings as Cog, Code2, ToggleLeft, ToggleRight,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import CrmTab from "../components/admin/CrmTab";
import CmsTab from "../components/admin/CmsTab";
import MediaTab from "../components/admin/MediaTab";
import CurriculumTab from "../components/admin/CurriculumTab";
import SeoTab from "../components/admin/SeoTab";
import EmailTemplatesTab from "../components/admin/EmailTemplatesTab";
import SettingsTab from "../components/admin/SettingsTab";
import DevBuildTab from "../components/admin/DevBuildTab";
import MarketingLeadsCard from "../components/admin/MarketingLeadsCard";
import WixSubscribersCard from "../components/admin/WixSubscribersCard";

const TABS = [
  { id: "crm",         label: "CRM",             Icon: Users2,       Comp: CrmTab },
  { id: "cms",         label: "Content (CMS)",   Icon: FileEdit,     Comp: CmsTab },
  { id: "media",       label: "Media Library",   Icon: ImageIcon,    Comp: MediaTab },
  { id: "curriculum",  label: "Curriculum",      Icon: GraduationCap,Comp: CurriculumTab },
  { id: "seo",         label: "SEO",             Icon: Globe,        Comp: SeoTab },
  { id: "email",       label: "Email Templates", Icon: Mail,         Comp: EmailTemplatesTab },
  { id: "settings",    label: "Settings",        Icon: Cog,          Comp: SettingsTab },
  { id: "devbuild",    label: "Dev Build Console", Icon: Code2,      Comp: DevBuildTab },
];

const Inner = () => {
  const { me } = usePortal();
  const [active, setActive] = useState("crm");
  const [demoMode, setDemoMode] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  useEffect(() => {
    if (me?.role !== "super_admin") return;
    api.get("/admin/system/demo-mode")
      .then(r => setDemoMode(!!r.data?.enabled))
      .catch(() => {});
  }, [me]);

  const toggleDemoMode = async () => {
    setDemoBusy(true);
    try {
      const r = await api.post("/admin/system/demo-mode", { enabled: !demoMode });
      setDemoMode(!!r.data?.enabled);
      toast.success(`Demo Mode ${r.data?.enabled ? "ENABLED" : "DISABLED"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update demo mode");
    }
    setDemoBusy(false);
  };

  if (!me) return null;
  if (me.role !== "super_admin") {
    return <Navigate to="/portal" replace />;
  }

  const ActiveComp = TABS.find(t => t.id === active)?.Comp || CrmTab;

  return (
    <div className="space-y-6" data-testid="admin-hub">
      {/* Demo Mode banner when ON */}
      {demoMode && (
        <div
          className="rounded-md border-2 border-gold bg-gold/10 px-5 py-3 text-gold flex items-center gap-3"
          data-testid="admin-demo-banner"
        >
          <ToggleRight size={18} />
          <div className="font-medium text-sm">
            Demo Mode Active — Sales users are operating in demo environment.
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Super Admin</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Admin Hub</h1>
          <p className="text-cream-dim text-sm mt-2 max-w-2xl">
            Centralized control for customer relationships, content, media, curriculum, SEO,
            email templates, platform settings and code build submissions.
          </p>
        </div>

        {/* Demo Mode toggle card */}
        <div className="glass p-4 min-w-[260px]" data-testid="admin-demo-toggle-card">
          <div className="text-[11px] uppercase tracking-[0.22em] text-electric">Platform Mode</div>
          <button
            type="button"
            onClick={toggleDemoMode}
            disabled={demoBusy}
            data-testid="admin-demo-toggle-btn"
            className={`mt-2 flex items-center justify-between w-full p-2 rounded transition-colors ${
              demoMode ? "bg-gold/15 border border-gold" : "bg-navy-deep border border-gold-dim/30"
            }`}
          >
            <span className="font-medium text-sm text-cream">
              Demo Mode <span className={demoMode ? "text-gold" : "text-cream-dim"}>{demoMode ? "ON" : "OFF"}</span>
            </span>
            {demoMode
              ? <ToggleRight size={22} className="text-gold" />
              : <ToggleLeft size={22} className="text-cream-dim" />}
          </button>
          <div className="text-[10px] text-cream-dim mt-2">
            When ON, Sales users see simulated datasets platform-wide.
          </div>
        </div>
      </div>

      {/* Marketing Leads KPI card */}
      <MarketingLeadsCard />

      {/* Wix Subscribers card */}
      <WixSubscribersCard />

      {/* Tab bar */}
      <div className="glass p-1.5 flex flex-wrap gap-1" data-testid="admin-hub-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setActive(id)}
            data-testid={`admin-tab-${id}`}
            className={`flex items-center gap-2 px-3 py-2 rounded text-xs uppercase tracking-[0.14em] transition-colors ${
              active === id
                ? "bg-navy-soft text-gold border border-gold"
                : "text-cream-dim hover:text-gold border border-transparent"
            }`}>
            <Icon size={14} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <ActiveComp />
    </div>
  );
};

const PortalAdminHub = () => <PortalLayout><Inner /></PortalLayout>;
export default PortalAdminHub;
